import type { ReactNode } from "react";
import BooksIcon from "../assets/Icon-Red_Hat-Books_multiple-A-Red-RGB.png";
import CertificationIcon from "../assets/Icon-Red_Hat-Certification-A-Red-RGB.png";
import EnvelopIcon from "../assets/Icon-Red_Hat-Envelope-A-Red-RGB.png";
import FlagIcon from "../assets/Icon-Red_Hat-Flag-A-Red-RGB.png";
import GraduationCap from "../assets/Icon-Red_Hat-Graduation_cap-A-Red-RGB.png";
import KioskIcon from "../assets/Icon-Red_Hat-KoaLA_kiosk-A-Red-RGB.png";
import MoneyStackIcon from "../assets/Icon-Red_Hat-Money_stack-A-Red-RGB.png";
import SupportIcon from "../assets/Icon-Red_Hat-Support-A-Red-RGB.png";
import userIcon from "../assets/Icon-Red_Hat-User-A-Red-RGB.png";
import WebsiteIcon from "../assets/Icon-Red_Hat-Website_system-A-Red-RGB.png";
import { CloudCard } from "../components/Dashboard/cards/CloudCard";
import { HardwareCard } from "../components/Dashboard/cards/HardwareCard";
import { LowGradeImagesCard } from "../components/Dashboard/cards/LowGradeImagesCard";
import { ManageMyCompanyCard } from "../components/Dashboard/cards/ManageMyCompanyCard";
import { MyCompanyCard } from "../components/Dashboard/cards/MyCompanyCard";
import { OtherResourcesCard } from "../components/Dashboard/cards/OtherResourcesCard";
import { PartnerSubscriptionsCard } from "../components/Dashboard/cards/PartnerSubscriptionsCard";
import PartnerTrainingCard from "../components/Dashboard/cards/PartnerTrainingCard";
import ProductsCard from "../components/Dashboard/cards/ProductsCard/ProductsCard";
import { SalesAndBusinessPartners } from "../components/Dashboard/cards/SalesAndBusinessPartners";
import { SupportCard } from "../components/Dashboard/cards/SupportCard";
import { WebinarsCard } from "../components/Dashboard/cards/WebinarsCard";
import { DashboardCard } from "../components/Dashboard/shared/DashboardCard";
import Env from "../utils/env";
import type { IPartnerInfo, IPartnershipStatus } from "./apiconstants-pfs";
import type { ITotalImagesCount } from "./apiconstants-pyxis";
import { EMAIL_PREFERENCES } from "./DashBoardUrls";

export enum CardTypeCode {
	ProductListings = "product_listings",
	SalesBusinessPartners = "sales_business_partners",
	LowGradeImages = "low_grade_images",
	HardwarePartners = "hardware_partners",
	CloudPartners = "cloud_partners",
	PartnerTraining = "partner_training",
	Webinars = "webinars",
	SupportCard = "support_card",
	ContentCenter = "content_center",
	DemoPlatform = "demo_platform",
	EmailPreferences = "email_preferences",
	UserAccount = "user_account",
	PartnerSubscriptions = "partner_subscriptions",
	SoftwareCertification = "software_certification",
	DealRegistration = "deal_registration",
	MarketDevelopmentFunds = "market_development_funds",
	TrainingPortal = "training_portal",
	PartnerWebsite = "partner_website",
	PartnerSupport = "partner_support",
	OtherResources = "other_resources",
	ManageMyCompany = "manage_my_company",
	MyCompany = "my_company",
}

export interface DragHandleProps {
	attributes: Record<string, any>;
	listeners: Record<string, any>;
}

export interface ICardRegistryEntry {
	cardTypeCode: CardTypeCode;
	renderCard: (dragHandleProps?: DragHandleProps | undefined) => ReactNode;
	isVisible: (
		status: IPartnershipStatus,
		info: IPartnerInfo | undefined,
		loading: boolean,
		totalImagesCount?: ITotalImagesCount,
	) => boolean;
}

export const CardRegistry: ICardRegistryEntry[] = [
	{
		cardTypeCode: CardTypeCode.ProductListings,
		renderCard: (dh) => <ProductsCard dragHandleProps={dh} />,
		isVisible: (status) => !!status?.buildPartner,
	},
	{
		cardTypeCode: CardTypeCode.SalesBusinessPartners,
		renderCard: (dh) => <SalesAndBusinessPartners dragHandleProps={dh} />,
		isVisible: (status) => !!status?.businessPartner,
	},
	{
		cardTypeCode: CardTypeCode.LowGradeImages,
		renderCard: (dh) => <LowGradeImagesCard dragHandleProps={dh} />,
		isVisible: (status, _info, _loading, totalImagesCount) =>
			!!status?.buildPartner && totalImagesCount?.data?.totalImagesCountByOrgId?.total !== 0,
	},
	{
		cardTypeCode: CardTypeCode.HardwarePartners,
		renderCard: (dh) => <HardwareCard dragHandleProps={dh} />,
		isVisible: (status) => !!status?.hardwarePartner,
	},
	{
		cardTypeCode: CardTypeCode.CloudPartners,
		renderCard: (dh) => <CloudCard dragHandleProps={dh} />,
		isVisible: (status) => !!status?.cloudPartner,
	},
	{
		cardTypeCode: CardTypeCode.PartnerTraining,
		renderCard: (dh) => <PartnerTrainingCard dragHandleProps={dh} />,
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.Webinars,
		renderCard: (dh) => <WebinarsCard dragHandleProps={dh} />,
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.SupportCard,
		renderCard: (dh) => <SupportCard dragHandleProps={dh} />,
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.ContentCenter,
		renderCard: (dh) => (
			<DashboardCard
				icon={BooksIcon}
				title="Partner content library"
				description="Access a library of marketing, sales & technical content."
				footerLinks={[{ label: "Browse digital assets", href: `${Env.ContentCenterPortals[Env.getEnvName()]}` }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.DemoPlatform,
		renderCard: (dh) => (
			<DashboardCard
				icon={KioskIcon}
				title="Red Hat Demo Platform"
				description="Hands-on assets for learning and showcasing Red Hat solutions"
				footerLinks={[
					{ label: "Access demo platform", href: "https://partner.demo.redhat.com/?oauthProviderRedirect=sso" },
				]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.EmailPreferences,
		renderCard: (dh) => (
			<DashboardCard
				icon={EnvelopIcon}
				title="Email preference center"
				description="Select areas of interest for your personalized emails from Red Hat."
				footerLinks={[{ label: "Manage preferences", href: `${EMAIL_PREFERENCES}` }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.UserAccount,
		renderCard: (dh) => (
			<DashboardCard
				icon={userIcon}
				title="User account"
				description="Edit your contact info, password, location preferences, and errata notifications"
				footerLinks={[
					{
						label: "Access my account",
						href: `${Env.SSOPortal[Env.getEnvName()]}auth/realms/redhat-external/account/`,
					},
				]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.PartnerSubscriptions,
		renderCard: (dh) => <PartnerSubscriptionsCard dragHandleProps={dh} />,
		isVisible: (_status, info, loading) =>
			loading ||
			info?.subscriptionStatus?.active ||
			info?.subscriptionStatus?.available ||
			info?.subscriptionStatus?.statusDetail !== "SUCCESS",
	},
	{
		cardTypeCode: CardTypeCode.SoftwareCertification,
		renderCard: (dh) => (
			<DashboardCard
				icon={CertificationIcon}
				title="Software certification"
				description="Access tools to certify your products on Red Hat platforms"
				footerLinks={[
					{ label: "Certify your software", href: `${Env.ConnectPortalUrls[Env.getEnvName()]}account/dashboard` },
				]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.DealRegistration,
		renderCard: (dh) => (
			<DashboardCard
				icon={FlagIcon}
				title="Deal registration (LATAM & APAC)"
				description="Register opportunities and receive special incentives when deals are closed"
				primaryAction={{ label: "Register an opportunity", href: "https://partnercenter.redhat.com/RegistrationHome" }}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.MarketDevelopmentFunds,
		renderCard: (dh) => (
			<DashboardCard
				icon={MoneyStackIcon}
				title="Market development funds"
				description="Plan, prioritize, and manage MDF activities"
				footerLinks={[{ label: "Manage MDF", href: "https://connect.redhat.com/account/dashboard" }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.TrainingPortal,
		renderCard: (dh) => (
			<DashboardCard
				icon={GraduationCap}
				title="Red Hat Partner Training Portal"
				description="Role-based training for sellers, technical sellers, & delivery specialists"
				footerLinks={[{ label: "Start learning", href: "https://training-lms.redhat.com/sso/saml/auth/rhopen" }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.PartnerWebsite,
		renderCard: (dh) => (
			<DashboardCard
				icon={WebsiteIcon}
				title="Partner website home page"
				description="Access information about partner programs and benefits, and the partner blog"
				footerLinks={[{ label: "Access partner website", href: `${Env.ConnectPortalUrls[Env.getEnvName()]}` }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.PartnerSupport,
		renderCard: (dh) => (
			<DashboardCard
				icon={SupportIcon}
				title="Partner support"
				description="For any and all questions about your partnering activities with Red Hat"
				footerLinks={[{ label: "Get help", href: `${Env.ConnectPortalUrls[Env.getEnvName()]}en/support` }]}
				dragHandleProps={dh}
			/>
		),
		isVisible: () => false,
	},
	{
		cardTypeCode: CardTypeCode.OtherResources,
		renderCard: (dh) => <OtherResourcesCard dragHandleProps={dh} />,
		isVisible: () => true,
	},
	{
		cardTypeCode: CardTypeCode.ManageMyCompany,
		renderCard: (dh) => <ManageMyCompanyCard dragHandleProps={dh} />,
		isVisible: (_status, info, loading) => loading || !!info?.orgAdmin,
	},
	{
		cardTypeCode: CardTypeCode.MyCompany,
		renderCard: (dh) => <MyCompanyCard dragHandleProps={dh} />,
		isVisible: (status) => !!status?.isPartner,
	},
];
